import React, { useEffect, useState, useRef } from "react";

import { useSelector, useDispatch } from "react-redux";
import { fetchRawDetails } from "./SwitchDisposition.actions";
import { useLocation, useParams } from "react-router-dom";

import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
  
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Alert from "react-bootstrap/Alert";

import { sendAuthenticatedAsyncRequest } from "../../../services/AsyncRequestService";
import { getLoggedInUser } from "../../../services/AuthService";
import { SWITCH_DISPOSITION } from "rbac/RbacContentProvider";
import Colors from "../../../lookup/Colors.json";
import DismissableModal from "../../../components/DismissableModal";
import UsabilityContext from "lookup/UsabilityContext";
import SwitchDispositionForm from "components/SwitchDisposition/SwitchDispositionForm";
import { WorkflowConstants } from "lookup/WorkflowConstants";
import PersonBadge from "components/PersonBadge";
import { FormSelect, OverlayTrigger, Tooltip } from "react-bootstrap";
import InconclusiveBadge from "components/InconclusiveBadge";

const detectedDispositionColors = {
  ACCEPTED: Colors["rm-success"],
  DECLINED: Colors["rm-danger"],
  UNKNOWN: Colors["rm-warning"],
};

export const RAW_STATUSES = {
  READY: "READY",
  UNCLAIMED: "UNCLAIMED",
  CLAIMED: "CLAIMED",
  CONFIRMED: "CONFIRMED",
};

const RawSubmitForReview = (props) => {
  const [isCommentValid, setIsCommentValid] = useState(false);
  const [comments, setComments] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [failure, setFailure] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchUsers = () => {
    const context = "raw";
    sendAuthenticatedAsyncRequest(
      `/user?exclude_requester=true&context=${context}`,
      "GET",
      null,
        (r) => setUsersList(r.data),
        (r) => setFailure(false)
      );
    };
    fetchUsers();
  }, []);

  const submitForReview = (closeModal) => {
    if (isCommentValid && isUserSelected) {
      props.triggerEventOnRaw(props.rawId, "ASSIGN", {
        reviewerId: selectedUserId !== "PAW" ? selectedUserId : null,
        submitToPaw: selectedUserId === "PAW",
        notesForReviewer: { note: comments },
      });
      closeModal();
    }
  };

  const customFooterContent = (
    <div>
      <Button
        id="switchdisposition-sfr-modal-btn-cancel"
        variant="outline-dark"
        onClick={() => modalRef.current.handleClose()}
      >
        Cancel
      </Button>
      <Button
        id="switchdisposition-sfr-modal-btn-submit"
        className="left-spacer"
        variant="warning"
        onClick={() => submitForReview(modalRef.current.handleClose)}
      >
        Yes, submit this response for review
      </Button>
    </div>
  );

  return (
    <DismissableModal
      ref={modalRef}
      customFooterContent={customFooterContent}
      openModalButtonText="Submit for Review"
      openModalButtonStyle="warning"
      openModalButtonBlock={true}
      openModalButtonDisabled={props.disallowEditing}
      openModalButtonId="switchdisposition-btn-sfr"
      title="Submit Response for Review"
    >
      <p className="confirmation-text">
        You have selected to <b>submit</b> this response for review.
      </p>
      <p className="text-muted">
        Once you submit this response for review, the reviewer will claim this
        RAW and would be able to change its status.
      </p>
      {usersList.length === 0 ? (
        failure ? (
          <p className="text-danger"> Failed to load operators</p>
        ) : (
          <p className="text-info"> Loading Operators ...</p>
        )
      ) : (
        <Form.Group>
          <Form.Label>Select Reviewer</Form.Label>
          <FormSelect
            id="switchdisposition-sfr-reviewer"
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setIsUserSelected(e.target.value !== "");
            }}
          >
            <option value="">Click to select</option>
            {usersList.map((u, k) => (
              <option value={u.id} key={k}>
                {u.name}
              </option>
            ))}
            {SWITCH_DISPOSITION.provideSubmitToPaw(
              props.contextIsRaw
                ? UsabilityContext.RAW
                : UsabilityContext.SWIM
            )}
          </FormSelect>
          {!isUserSelected ? (
            <span className="text-danger smaller-feedback">
              Selecting a Reviewer is mandatory!
            </span>
          ) : (
            <span className="text-success smaller-feedback">Looks good!</span>
          )}
        </Form.Group>
      )}
      <Form.Group>
        <Form.Label>Please enter comments for reviewer</Form.Label>
        <Form.Control
          id="switchdisposition-sfr-comments"
          as="textarea"
          rows="3"
          value={comments}
          onChange={(e) => {
            setComments(e.target.value);
            setIsCommentValid(e.target.value !== "");
          }}
        />
        {!isCommentValid ? (
          <span className="text-danger smaller-feedback">
            Comments are mandatory!
          </span>
        ) : (
          <span className="text-success smaller-feedback">Looks good!</span>
        )}
      </Form.Group>
    </DismissableModal>
  );
};
export { RawSubmitForReview };

const SwitchDisposition = () => {
  const { rawId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const { rawDetails, rawDetailsStatus } = useSelector((state) => state.switchDisposition);

  useEffect(() => {
    dispatch(fetchRawDetails(rawId));
  }, [dispatch, rawId]);

  const CONTEXT = location.pathname.startsWith("/raw") || (location.state && location.state.context === UsabilityContext.RAW)
    ? UsabilityContext.RAW
    : UsabilityContext.SWIM;

  const SELECTABLE_DISPOSITIONS = SWITCH_DISPOSITION.provideSelectableDispositions(CONTEXT);

  const isOwnedByLoggedInUser = (details) => details.ownerId === getLoggedInUser().uuid;
  const isOwnedByAnyone = (details) => details.ownerId !== 0 && details.ownerId !== null;

  const renderOwnerInformation = (details) => {
    if (details.status === RAW_STATUSES.CONFIRMED) {
      return (
        <Alert variant="info">
          Disposition confirmed by{" "}
          <PersonBadge variant="info" name={details.ownerName} />
          <strong id="dispositionConfirmedBy">
            {details.ownerName || "Anonymous"}
          </strong>
        </Alert>
      );
    } else if (!isOwnedByAnyone(details)) {
      return <Alert variant="danger">Unclaimed</Alert>;
    } else {
      let variant = isOwnedByLoggedInUser(details) ? "success" : "warning";
      return (
        <Alert variant={variant}>
          {details.referrerId ? (
            <span>
              Referred by{" "}
              <PersonBadge variant={variant} name={details.referrerName} />
              <strong id="dispositionReferredBy">
                {details.referrerName}
              </strong>{" "}
              to
              <PersonBadge variant={variant} name={details.ownerName} />
              <strong id="dispositionOwner">{details.ownerName}</strong>
              <br />
              <span id="dispositionNotes" style={{ fontSize: "0.9em" }}>
                <strong>Notes: </strong> {details.statusNote}
              </span>
            </span>
          ) : (
            <span id="dispositionClaimedByContainer">
              {details.autoClaimed ? "Auto-c" : "C"}laimed by{" "}
              <PersonBadge variant={variant} name={details.ownerName} />
              <span id="dispositionOwner">{details.ownerName}</span>
            </span>
          )}
        </Alert>
      );
    }
  };

  const changeDisposition = location.state?.changeDisposition ?? false;
  const isForAccepted = location.state?.isForAccepted ?? false;
  const isForDeclined = location.state?.isForDeclined ?? false;
  const isPrescriberResponseFaxAvailable = location.state?.isPrescriberResponseFaxAvailable ?? false;

  const selectableDispositions = { ...SELECTABLE_DISPOSITIONS };

  if (rawDetails) {
    if (
      rawDetails &&
      (rawDetails.wfItemStatus === WorkflowConstants.DELIVERED_PRESCRIBER_FAX ||
        rawDetails.wfItemStatus ===
          WorkflowConstants.MISSING_PRESCRIBER_CONTACT_INFO)
    ) {
      delete selectableDispositions.ACCEPTED;
    }
    if (isForAccepted) {
      delete selectableDispositions.SUBMITTED_VIA_EHR;
      delete selectableDispositions.MEMBER_ON_TARGET;
    }
    if (isForDeclined) {
      delete selectableDispositions.DECLINED;
    }
    if (changeDisposition && !isPrescriberResponseFaxAvailable) {
      delete selectableDispositions.ACCEPTED;
    }
  }

  const contextIsRaw = CONTEXT === UsabilityContext.RAW;

  if (rawDetailsStatus === "failure") return "Failed to fetch RAW details";
  if (rawDetailsStatus === "requested" || !rawDetails) return "Fetching Response Assessment Workflow ... ";

    const {
      globalSwitchId,
      detectedDisposition,
      disposition,
      targetOptions,
      isInconclusive,
      responseTypeList,
      wfItemStatus,
      wfItemId,
    } = rawDetails;

    const isRawConfirmed = rawDetails.status === RAW_STATUSES.CONFIRMED;
    const disableActions = (!isOwnedByLoggedInUser(rawDetails) && !changeDisposition) ||
                           (isRawConfirmed && !changeDisposition) ||
                           (wfItemStatus === WorkflowConstants.TARGET_NOT_FILLED);
    
    const pdfUrl =
      rawDetails.wfItemStatus === WorkflowConstants.DELIVERED_PRESCRIBER_FAX
        ? `/pdf/${rawDetails.customerId}/prescriber-inbound-or-generated-redacted?switch_id=${rawDetails.switchId}`
        : `/pdf/prescriber-inbound-redacted-fax/${wfItemId}`;
    return (
      <Container fluid>
        <Row>
          <Col md={5}>
            {contextIsRaw ? (
              <Breadcrumb>
                <Breadcrumb.Item href="/raw/list">
                  Response List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  Switch {rawDetails.globalSwitchId}
                </Breadcrumb.Item>
              </Breadcrumb>
            ) : (
              <Breadcrumb>
                <Breadcrumb.Item href={`/switch/list`}>
                  Switch List
                </Breadcrumb.Item>
                <Breadcrumb.Item href={`/switch/${wfItemId}`}>
                  Switch {globalSwitchId}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Disposition</Breadcrumb.Item>
              </Breadcrumb>
            )}
            {rawDetails.workflowItemStatus === "PENDING_RESPONSE_REVIEW" ? (
              <Row>
                <Col xs={12}>
                  <p style={{ fontSize: "1.2em" }}>
                    <Badge bg="warning" pill>
                      Forwarded from SWIM
                    </Badge>{" "}
                    &nbsp;
                    {rawDetails.forwardToPawNote ? (
                      <small>
                        <strong>Comment:</strong> {rawDetails.forwardToPawNote}
                      </small>
                    ) : null}
                  </p>
                </Col>
              </Row>
            ) : null}

            <Row className="tiny-top-spacer">
              <Col id="dispositionOwnerInfo" md={12}>
                {renderOwnerInformation(rawDetails)}
              </Col>
              <Col
                id="dispositionForContainer"
                md={12}
                style={{ fontSize: "1.5em" }}
              >
                <span className="text-muted">Disposition for </span>
                {contextIsRaw ? (
                  <a
                    href={`/raw/${rawId}/wf/${wfItemId}`}
                  >
                    <u>Switch # {rawDetails.globalSwitchId}</u>
                  </a>
                ) : (
                  <span>
                    Switch # <span id="globalSwitchId">{globalSwitchId}</span>
                  </span>
                )}
                &nbsp;
                {rawDetails.isControlled ? (
                  <Badge bg="warning" pill>
                    Controlled
                  </Badge>
                ) : null}
              </Col>
            </Row>
            <hr />
            {isRawConfirmed && disposition && !changeDisposition ? (
              <div
                id="confirmedDispositionContainer"
                style={{
                  border: `2px solid ${
                    detectedDispositionColors[disposition.disposition]
                  }`,
                  borderRadius: "10px",
                  padding: 10,
                }}
                className="text-center top-spacer bottom-spacer"
              >
                Confirmed Disposition:{" "}
                <span
                  id="confirmedDisposition"
                  style={{
                    fontSize: "1.2em",
                    letterSpacing: "1px",
                    textDecoration: "underline",
                  }}
                >
                  {disposition.disposition}
                  <InconclusiveBadge
                    inconclusiveResult={disposition.inconclusiveResult}
                    responseTypeList={disposition.responseTypeList}
                  />
                </span>
              </div>
            ) : detectedDisposition && !changeDisposition ? (
              <div
                id="switchDispositionContainer"
                style={{
                  border: `2px solid ${detectedDispositionColors[detectedDisposition]}`,
                  borderRadius: "10px",
                  padding: 10,
                }}
                className="text-center top-spacer bottom-spacer"
              >
                Auto Accept Detection:{" "}
                <span
                  id="detectedDisposition"
                  style={{
                    fontSize: "1.2em",
                    letterSpacing: "1px",
                    textDecoration: "underline",
                  }}
                >
                  {detectedDisposition}
                  <InconclusiveBadge
                    inconclusiveResult={isInconclusive}
                    responseTypeList={responseTypeList}
                  />
                </span>
              </div>
            ) : null}
            <SwitchDispositionForm
              disposition={disposition}
              isRawConfirmed={isRawConfirmed}
              disableActions={disableActions}
              globalSwitchId={globalSwitchId}
              selectableDispositions={selectableDispositions}
              rawDetails={rawDetails}
              targetOptions={targetOptions}
              contextIsRaw={contextIsRaw}
              isSubmissionRequestFromSwim={location.state?.isSubmissionRequestFromSwim ?? false}
              changeDisposition={
                location.state?.changeDisposition ?? false
              }
            />
          </Col>

          <Col md={7}>
            {
              <ConfigurablePdfViewer
                height="85vh"
                width="100%"
                url={pdfUrl}
              />
            }
          </Col>
        </Row>
      </Container>
    );
  }

export default SwitchDisposition;
